import * as THREE from 'three';
import { self, skill, stage } from '../../content/classes/kit';
import type { Game } from '../../game/Game';
import { setRideProvider } from '../../render/ZoneVisuals';
import type { Actor } from '../../sim/actor';
import { registry } from '../../sim/registry';
import { registerStatus } from '../../sim/status';
import { removeStatus } from '../../sim/statusSystem';

/**
 * Mounts (Lost Ark's summoned rides): a short summon cast gives a `mounted`
 * status (+70% movement, beyond the normal speed cap). Using any skill, dash
 * or battle item, taking damage, or entering combat content dismounts. Fields,
 * towns and islands only. The status replicates, so everyone sees the ride.
 */

export interface MountDef {
  id: string;
  name: string;
  model: string;
  scale: number;
  seat: THREE.Vector3;
  /** Character level that unlocks it. */
  level: number;
}

export const MOUNTS: MountDef[] = [
  { id: 'direwolf', name: 'Ashen Direwolf', model: 'mob_wolf', scale: 1.45, seat: new THREE.Vector3(0, 1.32, -0.18), level: 5 },
  { id: 'charger', name: 'Thornback Charger', model: 'mob_boar', scale: 1.4, seat: new THREE.Vector3(0, 1.3, -0.1), level: 18 },
];

export const MOUNT_SPEED = 0.7;
export const SUMMON_TIME = 1.2;
const MOUNT_ZONES = new Set(['field', 'town', 'island', 'haven']);
const SKILL_ID = 'mount_summon';

export const MOUNT_SKILL = skill({
  id: SKILL_ID,
  name: 'Summon Mount',
  description: 'Call your mount.',
  castType: 'normal',
  castTime: SUMMON_TIME,
  cooldown: 1,
  mana: 0,
  kind: 'monster',
  stages: [stage('Buff', 0.25, [self(0.05, { id: 'mounted', duration: 36_000 })], { cancelAt: 0.1 })],
  superArmor: 'none',
  stagger: 'none',
  positional: 'none',
});

/** The mount a character rides (the last one they picked among those unlocked). */
export function mountFor(level: number, picked: string | undefined): MountDef | null {
  const open = MOUNTS.filter((m) => level >= m.level);
  return open.find((m) => m.id === picked) ?? open[0] ?? null;
}

function dismount(game: Game, p: Actor): void {
  const z = game.zone;
  if (z && p.hasStatus('mounted')) removeStatus(z.world, p, 'mounted');
}

function toggle(game: Game): void {
  const z = game.zone;
  const p = game.player;
  const prog = game.progression;
  if (!z || !p || !prog || !p.alive) return;
  if (p.hasStatus('mounted')) {
    dismount(game, p);
    return;
  }
  const m = mountFor(prog.char.level, prog.char.flags.mount as string | undefined);
  if (!m) {
    game.notify(`Mounts unlock at level ${MOUNTS[0]?.level ?? 5}.`, '#e5484d');
    return;
  }
  if (!MOUNT_ZONES.has(z.layout.kind)) {
    game.notify('You cannot summon a mount here.', '#e5484d');
    return;
  }
  if (p.combatIdle < 3) {
    game.notify('You cannot summon a mount in combat.', '#e5484d');
    return;
  }
  let s = p.skills.get(SKILL_ID);
  if (!s) {
    const def = registry.skill(SKILL_ID);
    s = { ...def, level: 1, dmgMult: 1, critRateAdd: 0, critDmgAdd: 0, atkSpeedMult: 1, vsWeakMult: 1, vsBossMult: 1, defPen: 0, damageReduction: 0, identityGainMult: 1, extraStatus: [], tripods: [] };
    p.skills.set(SKILL_ID, s);
  }
  z.world.useSkill(p, s, 'I4');
}

/** Cycles the appearance among unlocked mounts. */
export function nextMount(game: Game): MountDef | null {
  const prog = game.progression;
  if (!prog) return null;
  const open = MOUNTS.filter((m) => prog.char.level >= m.level);
  if (open.length === 0) return null;
  const cur = mountFor(prog.char.level, prog.char.flags.mount as string | undefined);
  const next = open[(open.findIndex((m) => m.id === cur?.id) + 1) % open.length] as MountDef;
  prog.char.flags.mount = next.id;
  return next;
}

export function install(game: Game): void {
  registerStatus({ id: 'mounted', name: 'Mounted', kind: 'buff', icon: 'mounted', description: `Riding: movement speed +${Math.round(MOUNT_SPEED * 100)}%.`, stacking: 'refresh', mods: { moveSpeedMult: MOUNT_SPEED }, color: 0xc9a449 });
  registry.registerSkill(MOUNT_SKILL);
  game.actionHandlers.set('mount', (g) => toggle(g));

  // Every peer draws riders from the replicated status; the rider's own pick sets the creature.
  setRideProvider((a) => {
    if (!a.hasStatus('mounted') || !a.player) return null;
    const picked = a.id === game.player?.id ? (game.progression?.char.flags.mount as string | undefined) : undefined;
    const m = mountFor(a.player.level, picked);
    return m ? { model: m.model, scale: m.scale, seat: m.seat, pose: 'Emote_Sit' } : null;
  });

  // Dismount rules (the local player only; the status change replicates).
  let lastHp = 0;
  let announced = false;
  game.systems.push((g) => {
    const p = g.player;
    const z = g.zone;
    const prog = g.progression;
    if (!p || !z || !prog) return;
    if (!announced && prog.char.level >= (MOUNTS[0]?.level ?? 5)) {
      announced = true;
      if (!prog.char.flags.mountHint) {
        prog.char.flags.mountHint = true;
        // A side notification, so it never covers the level-up banner that unlocked it.
        g.hud.toast(`Mount unlocked — press <b>${g.input.bindingLabel('mount')}</b> to ride`, '#c9a449');
      }
    }
    if (p.hasStatus('mounted')) {
      const acting = p.cast && p.cast.skill.id !== SKILL_ID;
      if (acting || p.hp < lastHp || !MOUNT_ZONES.has(z.layout.kind) || !p.alive) dismount(g, p);
    }
    lastHp = p.hp;
  });
  // A fresh zone (instances) always starts on foot.
  game.zoneSetups.push((g) => {
    lastHp = 0;
    if (g.player) dismount(g, g.player);
  });
}
